import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/utils'

/**
 * Constituent UI primitive: Button
 *
 * Variants:
 *   primary   — solid blue, used for one primary action per surface
 *   secondary — white with slate border, default non-primary action
 *   ghost     — no chrome, used inside other surfaces (cards, toolbars)
 *   danger    — solid rose, destructive actions only
 *
 * Sizes (touch targets meet ≥44px on every layout, including 320/390px
 * phones — sizes `sm` and `md` are both 11 (44px), `lg` is 12 (48px)):
 *   sm — compact inline (rows, chips) — still 44px tall for touch
 *   md — default
 *   lg — hero / form submit
 *
 * Pressed state: `aria-pressed` reflects `pressed` prop so toggle buttons
 * announce state to screen readers and pick up an inset ring without
 * overriding variant colors.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  pressed?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  fullWidth?: boolean
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:bg-slate-300 disabled:text-slate-500',
  secondary:
    'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 disabled:bg-slate-50 disabled:text-slate-400',
  ghost:
    'bg-transparent text-slate-700 hover:bg-slate-100 active:bg-slate-200 disabled:text-slate-400',
  danger:
    'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 disabled:bg-slate-300 disabled:text-slate-500',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // h-11 = 44px at every viewport — the sm breakpoint no longer scales
  // DOWN below the touch target because there is no layout where we
  // accept smaller primary controls.
  sm: 'h-11 px-3 text-xs',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  pressed,
  leadingIcon,
  trailingIcon,
  fullWidth,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading
  return (
    <button
      {...rest}
      type={type}
      aria-pressed={pressed === undefined ? undefined : pressed}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      className={cn(
        'relative inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        'disabled:cursor-not-allowed',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        // Inset selected ring — never overrides variant colors so a primary
        // pressed button still reads as primary blue.
        pressed ? 'ring-2 ring-inset ring-blue-500/70' : null,
        fullWidth ? 'w-full' : null,
        className,
      )}
    >
      {loading ? <ButtonSpinner /> : leadingIcon}
      <span className={cn(loading ? 'opacity-0' : null)}>{children}</span>
      {!loading && trailingIcon}
    </button>
  )
}

function ButtonSpinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
    />
  )
}

/**
 * Constituent UI primitive: IconButton
 *
 * Square hit area, screen-reader label is mandatory. Variants mirror Button
 * minus `ghost` (icon-only controls default to ghost chrome so they don't
 * compete visually with content cards). Every size meets the 44px touch
 * target even on a 320px viewport.
 */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md'
  pressed?: boolean
}

const ICON_VARIANT_CLASSES: Record<NonNullable<IconButtonProps['variant']>, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
  secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
}

const ICON_SIZE_CLASSES: Record<NonNullable<IconButtonProps['size']>, string> = {
  sm: 'h-11 w-11', // 44px — no smaller touch target
  md: 'h-11 w-11',
}

export function IconButton({
  label,
  variant = 'ghost',
  size = 'md',
  pressed,
  className,
  disabled,
  type = 'button',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-label={label}
      aria-pressed={pressed === undefined ? undefined : pressed}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded-lg transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
        'disabled:cursor-not-allowed disabled:opacity-60',
        ICON_VARIANT_CLASSES[variant],
        ICON_SIZE_CLASSES[size],
        pressed ? 'ring-2 ring-inset ring-blue-500/70' : null,
        className,
      )}
    >
      {children}
    </button>
  )
}
